// "What's your Top 5?" promo on Discover, for guests and members alike. It
// links straight to the existing creator (/top-movies), which works without an
// account. The art is five empty poster frames — never real posters — so it
// can't read as someone's picks.
//
// Impression: sent once per mount, when at least half the card has actually
// been on screen (IntersectionObserver). Browsers without it send none.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ANALYTICS_EVENTS } from '../../analytics';
import { trackTopMovies } from '../../analytics/topMoviesAnalytics';
import styles from './TopMoviesDiscoverCard.module.css';

export default function TopMoviesDiscoverCard({ loggedIn = false }) {
    const ref = useRef(null);
    const loggedInRef = useRef(loggedIn);
    loggedInRef.current = loggedIn;

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') return undefined;
        const observer = new window.IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_CARD_VIEWED, { surface: 'discover', logged_in: loggedInRef.current });
        }, { threshold: 0.5 });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={ref} className={styles.card} aria-labelledby="top5-discover-title" data-testid="top-movies-discover-card">
            <div className={styles.art} aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => <span key={i} className={styles.frame} />)}
            </div>
            <div className={styles.body}>
                <h2 id="top5-discover-title" className={styles.title}>What&apos;s Your Top 5? 🎬</h2>
                <p className={styles.text}>Everyone has five movies they&apos;d watch forever. Show us yours.</p>
                {/* Custom Rankings: a quiet secondary path, not a second promo. */}
                <p className={styles.text}>
                    <Link to="/rankings/new" state={{ source: 'discover' }} className={styles.secondaryLink}>Or rank anything — TV, anime, comedies →</Link>
                </p>
            </div>
            <Link
                to="/top-movies"
                className={styles.cta}
                onClick={() => trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_CARD_CLICKED, { surface: 'discover', logged_in: loggedIn })}
            >
                Make Your Top 5
            </Link>
        </section>
    );
}
